import { createAsyncThunk } from '@reduxjs/toolkit'
import jwtDecode from 'jwt-decode'
import RestClient from '../../../services/client/RestClient'
import Configuration from '../../../services/api/Configuration'
import CookiesStorageService from '../../../services/CookiesStorageService'
import AppRoutes from '../../../services/AppRoutes'
import BiometricAuthService from '../../../services/BiometricAuthService'
import { setPendingBiometricCredentials } from '../../../services/PendingBiometricCredentials'
import { loadTranslationsFile } from '../languageTranslation/languageThunk'

const userAuthLoginApi = Configuration.Auth_Endpoints
const logoutPath = `${Configuration.USER_MANAGEMENT_BASE_URL}/${Configuration.Auth_Endpoints.logout}`

export const userAuthResetAlertHandler = createAsyncThunk(
  'userAuth/userAuthResetAlertHandler',
  async () => ({}),
)
export const userAuthTokenDecode = createAsyncThunk(
  'userAuth/userAuthTokenDecode',
  async ({ queries }) => jwtDecode(queries.token),
)

export const userAuthLogin = createAsyncThunk(
  'userAuth/userAuthLogin',
  async ({ queries }, { rejectWithValue, dispatch }) => {
    const response = await RestClient.postRequest(userAuthLoginApi.login, queries)

    if (response.status >= 300) {
      return rejectWithValue({
        status: 401,
        message: response.message || response.status === 401
          ? 'Incorrect Login name  or password'
          : 'oops something went wrong please try again later',

        timestamp: response.timestamp,
        severity: 'error',
      })
    }

    CookiesStorageService.setUserData(response.data)

    // Handed to BiometricEnrollmentGate so it can offer to store them behind
    // Face ID. Kept in memory only and cleared as soon as the gate reads them.
    setPendingBiometricCredentials({
      email: queries.email,
      password: queries.password,
    })

    dispatch(loadTranslationsFile())

    return response.data
  },
)

export const userAuthLogout = createAsyncThunk(
  'userAuth/userAuthLogout',
  async (_, { dispatch }) => {
    const query = {
      token: CookiesStorageService.getAccessToken(),
      refresh_token: CookiesStorageService.getAccessRefreshToken(),
    }

    const response = await RestClient.postRequest(logoutPath, query)

    if (response.status === 200) {
      CookiesStorageService.clearCookies()
      dispatch(clearAuthorizedUser())
      if (response?.data?.redirectUrl) {
        window.location.href = response.data.redirectUrl
      }
    }

    return response.data
  },
)

export const userAuthResetPassword = createAsyncThunk(
  'userAuth/userAuthResetPassword',
  async ({ queries }, { rejectWithValue }) => {
    const response = await RestClient.postRequest(userAuthLoginApi.resetPasswordApply, queries)

    if (response.status > 201) {
      return rejectWithValue({
        status: 401,
        message: 'Incorrect password',
        timestamp: response.timestamp,
        severity: 'error',
      })
    }

    return {
      status: 201,
      message: 'Successfully updated your password',
    }
  },
)
export const sendForgetPasswordEmail = createAsyncThunk(
  'userAuth/sendForgetPasswordEmail',
  async ({ queries }, { rejectWithValue }) => {
    const response = await RestClient.postRequest(userAuthLoginApi.sendForgetPasswordEmail, queries)

    if (response.status >= 300) {
      return rejectWithValue(response)
    }
    return response.data
  },
)
export const validateResetPasswordToken = createAsyncThunk(
  'userAuth/validateResetPasswordToken',
  async ({ queries }, { rejectWithValue }) => {
    const response = await RestClient.postRequest(`${userAuthLoginApi.sendForgetPasswordEmail}/${queries}`)
    if (response.status >= 300) {
      return rejectWithValue(response)
    }
    return response.status
  },
)

export const userResetPassword = createAsyncThunk(
  'userAuth/userResetPassword',
  async ({ queries }, { rejectWithValue, getState }) => {
    const response = await RestClient.putRequest(userAuthLoginApi.sendForgetPasswordEmail, queries)
    const { language } = getState()
    if (response.status > 201) {
      return rejectWithValue({
        status: 401,
        message: response.message,
        timestamp: response.timestamp,
        severity: 'error',
      })
    }

    return {
      status: 201,
      message: language?.translations?.['Successfully updated your password'],
    }
  },
)

export const userAuthResetCheckToken = createAsyncThunk(
  'userAuth/userAuthResetCheckToken',
  async ({ queries }, { rejectWithValue }) => {
    const response = await RestClient.postRequest(
      `${userAuthLoginApi.resetPasswordValidate}?token=${queries}`,
    )

    const requestUrl = response.config.url

    if (requestUrl.includes('validation') && response.status > 201) {
      return rejectWithValue({
        status: 401,
        message: 'Reset mail is expired please contact system administrator to reset your password',
        timestamp: response.timestamp,
        severity: 'error',
      })
    }

    return {
      status: 200,
      message: 'Token is Valid',
    }
  },
)

export const userAuthSamlExchange = createAsyncThunk(
  'userAuth/userAuthSamlExchange',
  async ({ code }, { rejectWithValue, dispatch }) => {
    if (!code) {
      return rejectWithValue({
        message:
          'We could not find the SSO response in the link. Please start the Single Sign-On login again from the login page.',
        severity: 'error',
      })
    }

    const response = await RestClient.postRequest(userAuthLoginApi.samlExchange, { code })

    if (response?.status >= 300) {
      return rejectWithValue({
        message:
          'We could not complete your Single Sign-On login. Please try again from the login page or contact your administrator if the problem continues.',
        severity: 'error',
      })
    }

    CookiesStorageService.setUserData(response.data)
    await dispatch(loadTranslationsFile())

    const isFirstLogin = CookiesStorageService.getIsFirstLogin()
    const redirectPath = isFirstLogin ? AppRoutes.firstTimePassword : AppRoutes.landing

    return { redirectUrl: redirectPath }
  },
)

export const userAuthSamlLoginCheck = createAsyncThunk(
  'userAuth/userAuthSamlLoginCheck',
  async ({ domain }, { rejectWithValue }) => {
    const response = await RestClient.getRequest(userAuthLoginApi.samlLogin, {
      params: { domain },
      maxRedirects: 0,
      validateStatus: (status) => status === 200 || status === 302,
    })

    if (response?.status >= 300) {
      return rejectWithValue({
        status: 401,
        message: response.message,
        timestamp: response.timestamp,
        severity: 'error',
      })
    }

    window.location.href = response.data?.redirectUrl

    return {}
  },
)

/**
 * Resolves whether the email domain uses SSO (SAML). On success, redirects to the IdP.
 * If the domain is not SSO-enabled, returns { flow: 'PASSWORD' } without surfacing an error.
 * Uses the same `saml/login` endpoint as the dedicated SSO screen; inspects all status codes.
 */
export const userAuthResolveSsoByDomain = createAsyncThunk(
  'userAuth/userAuthResolveSsoByDomain',
  async ({ email }, { rejectWithValue }) => {
    const response = await RestClient.postRequest(userAuthLoginApi.samlLogin, {
      email,
    })

    const redirectFromBody = response.data?.redirect_url

    if (redirectFromBody) {
      window.location.assign(redirectFromBody)
      return { flow: 'SSO_REDIRECT' }
    }

    if (response.status >= 500) {
      return rejectWithValue({
        message:
          response.data?.message ||
          response.message ||
          'We could not reach the authentication service. Please try again later.',
        severity: 'error',
      })
    }

    return { flow: 'PASSWORD' }
  },
)

export const userAuthValidateToken = createAsyncThunk(
  'userAuth/userAuthValidateToken',
  async () => {
    await RestClient.postRequest(`${userAuthLoginApi.validateToken}`, {
      token: CookiesStorageService.getAccessToken(),
      refresh_token: CookiesStorageService.getAccessRefreshToken(),
    })

    return {
      status: 200,
      message: 'Token is Valid',
    }
  },
)

export const clearAlertStore = createAsyncThunk('userAuth/clearAlertStore', async () => ({}))

export const clearAuthorizedUser = createAsyncThunk(
  'userAuth/clearAuthorizedUser',
  async () => ({}),
)

export const userAuthChangePassword = createAsyncThunk(
  'userAuth/userAuthChangePassword',
  async ({ queries }, { rejectWithValue }) => {
    const response = await RestClient.patchRequest(userAuthLoginApi.editUser, queries)
    if (response.status > 201) {
      return rejectWithValue({
        status: 401,
        message: response.message || 'Incorrect Login name  or password',
        timestamp: response.timestamp,
        severity: 'error',
      })
    }

    // The biometric vault holds the previous password, so it is stale now.
    // Drop it rather than letting the next biometric login fail.
    await BiometricAuthService.clear()

    return {
      status: 201,
      message: 'Successfully updated your password',
    }
  },
)

export const userCustomerAdminAuthChangePassword = createAsyncThunk(
  'userAuth/userAuthChangePassword',
  async ({ queries, userId }, { rejectWithValue }) => {
    const response = await RestClient.putRequest(`companies/users/${userId}/modify-password`, queries)

    if (response.status > 201) {
      return rejectWithValue({
        status: 401,
        message: response.message || 'Incorrect Login name  or password',
        timestamp: response.timestamp,
        severity: 'error',
      })
    }

    return {
      status: 201,
      message: 'Successfully updated your password',
    }
  },
)

export const userAuthGetPolicies = createAsyncThunk(
  'userAuth/userAuthGetPolicies',
  async (_, { rejectWithValue }) => {
    const tenantId = CookiesStorageService.getTenantId()
    const response = await RestClient.getRequest(`${userAuthLoginApi.getPolicies}/${tenantId}`)
    if (response.status >= 300) {
      return rejectWithValue(response)
    }
    return response.data
  },
)

export const userAuthAcceptPolicies = createAsyncThunk(
  'userAuth/userAuthAcceptPolicies',
  async (_, { rejectWithValue }) => {
    const userId = CookiesStorageService.getUserId()
    const response = await RestClient.postRequest(`${userAuthLoginApi.acceptPolicies}`, { user_id: userId })
    if (response.status >= 300) {
      return rejectWithValue(response)
    }
    CookiesStorageService.setAcceptedPolicies(true)
    return response.data
  },
)
